import { FC, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useFormik } from 'formik';
import { Box, MenuItem, Grid, SelectChangeEvent } from '@mui/material';
import { createFieldWalkthrough, getTerritories } from '../../../fetch';
import { ILot, INeighborhood, ITerritory } from '../../../models';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { IAPIErrorModal } from '../../../models/apiError';
import { useHistory } from 'react-router-dom';
import { Page, PageTitleWithBack, SMBSelect } from '../../../components';
import { getCommonNeighborhoodLots, getProductionNeighborhoods } from '../../../fetch/lookups';

interface FormValues {
  territoryId: string;
  neighborhoodId: string;
  lotNumber: string;
}

const CreateFieldWalkthroughSchema = yup.object().shape({
  territoryId: yup.string().required(),
  neighborhoodId: yup.string().required(),
  lotNumber: yup.string().required()
});

export const CreateFieldWalkthroughPage: FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [territories, setTerritories] = useState<ITerritory[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>([]);
  const [lots, setLots] = useState<ILot[]>([]);

  const handleCreationError = (e: Error) => {
    const error = e as IAPIErrorModal;
    if (error?.Errors?.NeighborhoodId) {
      try {
        // The API returns the existing ID in a strange format: "Id: 12"
        const existingId = error?.Errors?.NeighborhoodId[1].match(/\d/g).join('');
        history.push(`/production/field-walkthroughs/${existingId}`);

        enqueueSnackbar('Redirecting to existing one...', {
          variant: 'info'
        });
        enqueueSnackbar('Field walkthrough already exists', {
          variant: 'warning'
        });
        return;
      } catch (e) {}
    }

    enqueueSnackbar(error?.Detail, {
      variant: 'error'
    });
  };

  const { isSubmitting, handleSubmit, values, dirty, isValid, setFieldValue } =
    useFormik<FormValues>({
      initialValues: {
        territoryId: '',
        neighborhoodId: '',
        lotNumber: ''
      },
      validationSchema: CreateFieldWalkthroughSchema,
      validateOnChange: true,
      onSubmit: async (values, actions) => {
        try {
          const fieldWalkthroughId = await createFieldWalkthrough({
            territoryId: +values.territoryId,
            neighborhoodId: +values.neighborhoodId,
            lotNumber: +values.lotNumber
          });
          actions.resetForm();
          history.push(`/production/field-walkthroughs/${fieldWalkthroughId}`);
        } catch (e) {
          handleCreationError(e);
        }
      }
    });

  const loadTerritories = async () => {
    const territoryData = await getTerritories();
    setTerritories(territoryData.records);
  };

  const loadNeighborhoods = async (territoryId: number) => {
    setNeighborhoods([]);
    const neighborhoodData = await getProductionNeighborhoods({ territoryId });
    setNeighborhoods(neighborhoodData.records);
  };

  const loadLots = async (neighborhoodId: number) => {
    setLots([]);
    const lotsData = await getCommonNeighborhoodLots({ neighborhoodId });
    setLots(lotsData);
  };

  const handleTerritoryChange = (e: SelectChangeEvent<string>) => {
    const territoryId = +e.target.value;
    setFieldValue('neighborhoodId', '');
    setFieldValue('lotNumber', '');
    loadNeighborhoods(territoryId);
  };

  const handleNeighborhoodChange = (e: SelectChangeEvent<string>) => {
    const neighborhoodId = +e.target.value;
    setFieldValue('lotNumber', '');
    loadLots(neighborhoodId);
  };

  useEffect(() => {
    const getData = async () => {
      loadTerritories();
    };
    getData();
  }, []);

  return (
    <Page title="Create Field Walkthrough">
      <PageTitleWithBack
        backText="Back to field walkthrough list"
        backUrl="/production/field-walkthroughs"
        title="Create Field Walkthrough"
        subTitle="Please select Territory, Neighborhood and Lot to get started with your walkthrough."
      />

      <form onSubmit={handleSubmit} autoComplete="off">
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <SMBSelect
              variant="outlined"
              label="Territory"
              value={values.territoryId}
              onChange={e => {
                handleTerritoryChange(e as any);
                setFieldValue('territoryId', e.target.value);
              }}
              handleClear={() => {
                setFieldValue('territoryId', '');
                setFieldValue('neighborhoodId', '');
                setFieldValue('lotNumber', '');
              }}
            >
              {territories.map(territory => (
                <MenuItem key={territory.territoryId} value={territory.territoryId}>
                  {territory.name}
                </MenuItem>
              ))}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} md={4}>
            <SMBSelect
              variant="outlined"
              label="Neighborhood"
              value={values.neighborhoodId}
              onChange={e => {
                handleNeighborhoodChange(e as any);
                setFieldValue('neighborhoodId', e.target.value);
              }}
              handleClear={() => {
                setFieldValue('neighborhoodId', '');
                setFieldValue('lotNumber', '');
              }}
            >
              {!neighborhoods.length && (
                <MenuItem value="">No neighborhoods available for this territory</MenuItem>
              )}
              {neighborhoods.map(hood => (
                <MenuItem key={hood.neighborhoodId} value={hood.neighborhoodId}>
                  {hood.name} Phase {hood.phase}
                </MenuItem>
              ))}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} md={4}>
            <SMBSelect
              variant="outlined"
              label="Job"
              value={values.lotNumber}
              onChange={e => {
                setFieldValue('lotNumber', e.target.value);
              }}
              handleClear={() => {
                setFieldValue('lotNumber', '');
              }}
            >
              {!lots.length && (
                <MenuItem value="">No lots available for this neighborhood</MenuItem>
              )}
              {lots.map((lot: ILot, index: number) => (
                <MenuItem key={`${index}`} value={lot.lotNumber}>
                  {lot.description}
                </MenuItem>
              ))}
            </SMBSelect>
          </Grid>
        </Grid>
        <Box margin="1rem 0" display="flex" alignItems="center" justifyContent="center">
          <LoadingButton
            className={classes.loadingButton}
            variant="contained"
            type="submit"
            color="primary"
            disabled={!dirty || isSubmitting || !isValid}
            loading={isSubmitting}
            loadingPosition="start"
          >
            <span>Start Walkthrough</span>
          </LoadingButton>
        </Box>
      </form>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  loadingButton: {
    '& .MuiLoadingButton-loadingIndicator': {
      position: 'static',
      marginRight: theme.spacing(0.5)
    }
  },
  selectLabel: {
    background: 'white'
  }
}));
